import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { devWarn } from "@/lib/log";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, RefreshCcw, X, ShoppingCart, AlertTriangle, IndianRupee, Truck, Inbox, Send, ListChecks, Loader2 } from "lucide-react";

const LS_KEY = "rimtask.briefing.dismissed";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * MorningBriefing — top-of-Dashboard card that shows a Gemini-crafted
 * daily summary + 5 key metric chips. Auto-dismisses per-day via localStorage.
 */
export default function MorningBriefing() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "manager";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Admin-only "Fire Briefing Now" flow — POSTs today's real metrics through
  // the approved WhatsApp template. Result is rendered inline so the admin
  // sees per-user status ("Sent", "Skipped", or the exact Meta error).
  const [firing, setFiring] = useState(false);
  const [fireResult, setFireResult] = useState(null);   // {sent, failed, skipped, results}

  useEffect(() => {
    // Skip fetching if already dismissed today
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw === todayIso()) { setDismissed(true); setLoading(false); return; }
    } catch (e) { devWarn("briefing dismiss check failed", e); }

    (async () => {
      try {
        const { data: b } = await api.get("/ai/briefing");
        setData(b);
      } catch (e) { devWarn("briefing load failed", e); }
      finally { setLoading(false); }
    })();
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(LS_KEY, todayIso()); } catch (e) { devWarn("briefing persist failed", e); }
    setDismissed(true);
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: b } = await api.get("/ai/briefing?refresh=true");
      setData(b);
    } catch (e) { devWarn("briefing refresh failed", e); }
    finally { setRefreshing(false); }
  };

  const fireBriefing = async () => {
    if (!window.confirm("Send the live morning briefing to every opted-in WhatsApp number right now?")) return;
    setFiring(true);
    setFireResult(null);
    try {
      // The endpoint refreshes today's metrics + summary inside
      // ``_send_briefing_to_user`` and sends via the approved template
      // (WA_TEMPLATE_MORNING_BRIEFING). No sample payload — real numbers.
      const { data: res } = await api.post("/ai/briefing/send-whatsapp", {});
      setFireResult(res);
      if (res?.sent > 0) {
        toast.success(`Sent to ${res.sent} number${res.sent === 1 ? "" : "s"}`);
      } else if (res?.failed > 0) {
        toast.error(`All ${res.failed} sends failed — expand for Meta error detail`);
      } else {
        toast.info("Nothing sent — check opt-ins");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "Fire briefing failed"));
    } finally { setFiring(false); }
  };

  if (dismissed || loading || !data) return null;

  const m = data.metrics || {};
  const chips = [
    { icon: ShoppingCart,  label: "Pending orders", value: m.pending_orders ?? 0, testid: "briefing-pending-orders" },
    { icon: Truck,         label: "Today's orders", value: m.todays_orders ?? 0,  testid: "briefing-todays-orders" },
    { icon: ListChecks,    label: "Active tasks",   value: m.active_tasks ?? 0,   testid: "briefing-active-tasks" },
    { icon: AlertTriangle, label: "Overdue tasks",  value: m.overdue_tasks ?? 0,  testid: "briefing-overdue", accent: (m.overdue_tasks || 0) > 0 },
    { icon: IndianRupee,   label: "Yesterday spend", value: `₹${Math.round(m.yesterday_spend || 0).toLocaleString("en-IN")}`, testid: "briefing-yesterday-spend" },
  ];

  return (
    <div
      data-testid="morning-briefing-card"
      className="mb-6 relative border-2 border-black dark:border-neutral-700 bg-white dark:bg-neutral-950"
    >
      {/* Header strip */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#0D0D0D] dark:bg-[#FF3B30] text-white">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em]">
          <Sparkles className="h-3 w-3" />
          RimBot / Morning briefing / {data.for_date}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing}
            data-testid="briefing-refresh"
            className="h-7 w-7 flex items-center justify-center hover:bg-white/10"
            aria-label="Refresh briefing"
            title="Regenerate"
          >
            <RefreshCcw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          </button>
          <button
            type="button"
            onClick={dismiss}
            data-testid="briefing-dismiss"
            className="h-7 w-7 flex items-center justify-center hover:bg-white/10"
            aria-label="Dismiss for today"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 md:p-5 space-y-4">
        <p data-testid="briefing-summary" className="text-sm md:text-base leading-relaxed text-black dark:text-white">
          {data.summary}
        </p>

        {/* Metric chips */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2" data-testid="briefing-chips">
          {chips.map((c) => (
            <div
              key={c.label}
              data-testid={c.testid}
              className={`border ${c.accent ? "border-[#FF3B30] text-[#FF3B30]" : "border-neutral-200 dark:border-neutral-800 text-black dark:text-white"} px-3 py-2`}
            >
              <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-neutral-500 dark:text-neutral-400">
                <c.icon className="h-3 w-3" /> {c.label}
              </div>
              <div className="font-heading font-black text-2xl leading-none mt-0.5">{c.value}</div>
            </div>
          ))}
        </div>

        {/* Top workload */}
        {m.team_workload_top?.length > 0 && (
          <div className="border-t border-neutral-200 dark:border-neutral-800 pt-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 dark:text-neutral-400 mb-1.5">
              / top workload
            </div>
            <div className="flex flex-wrap gap-2" data-testid="briefing-workload">
              {m.team_workload_top.map((w) => (
                <span key={w.name} className="text-xs border border-neutral-200 dark:border-neutral-800 px-2 py-0.5">
                  <b>{w.name}</b> · {w.open} open
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Admin-only manual dispatch. Uses the same real metrics rendered
            in the chips above; NOT a sample payload. */}
        {isAdmin && (
          <div className="border-t-2 border-dashed border-neutral-300 dark:border-neutral-700 pt-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">
                Live dispatch · uses <span className="font-bold text-[#0D0D0D] dark:text-white">wa_template_morning_briefing</span>
              </div>
              <button
                type="button"
                onClick={fireBriefing}
                disabled={firing}
                data-testid="briefing-fire-now"
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#DC262F] text-white bg-[#DC262F] hover:bg-[#0D0D0D] hover:border-[#0D0D0D] font-bold uppercase tracking-widest text-xs disabled:opacity-60 disabled:cursor-wait"
              >
                {firing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {firing ? "Sending…" : "Fire Briefing Now"}
              </button>
            </div>
            {fireResult && (
              <div className="mt-2 border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900" data-testid="briefing-fire-result">
                <div className="px-3 py-1.5 flex items-center gap-3 text-[11px] font-mono uppercase tracking-widest border-b border-neutral-200 dark:border-neutral-800">
                  <span>Attempted: <b>{fireResult.attempted}</b></span>
                  <span className="text-emerald-700">Sent: <b>{fireResult.sent}</b></span>
                  <span className="text-neutral-500">Skipped: <b>{fireResult.skipped}</b></span>
                  <span className="text-[#DC262F]">Failed: <b>{fireResult.failed}</b></span>
                </div>
                <ul className="text-[11px] font-mono divide-y divide-neutral-200 dark:divide-neutral-800">
                  {(fireResult.results || []).map((r) => (
                    <li key={r.user_id} className="px-3 py-1.5 flex items-start gap-2" data-testid={`briefing-fire-row-${r.user_id}`}>
                      <span className={`shrink-0 uppercase font-bold ${
                        r.status === "sent"    ? "text-emerald-700"
                      : r.status === "skipped" ? "text-neutral-500"
                      :                          "text-[#DC262F]"
                      }`}>{r.status}</span>
                      <span className="truncate flex-1">
                        {r.name || "?"} · {r.mobile || "no mobile"}
                        {r.template && <span className="ml-1 text-neutral-500">[{r.template}]</span>}
                        {r.message_id && <span className="ml-1 text-neutral-400 truncate">msg_id={r.message_id.slice(0, 20)}…</span>}
                        {r.meta_error?.detail && (
                          <span className="ml-1 text-[#DC262F]">· {r.meta_error.detail.slice(0, 160)}</span>
                        )}
                        {r.reason && r.status !== "sent" && !r.meta_error && (
                          <span className="ml-1 text-neutral-500">· {r.reason}</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
